/**
 * Product Card Management System - Moon Moon Agency v1.0
 * Handles quick-add panel interactions for product cards
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const CARD_CONFIG = {
    selectors: {
        productCard: '.mm-product-card',
        quickAddOpen: '[data-quick-add-open]',
        quickAddClose: '[data-quick-add-close]',
        quickAddContainer: '.mm-card-product-quick-add-container',
        quickAddTemplate: 'template[data-quick-add-template]'
    },

    get isDevelopment() {
        return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
    }
};

/*********************************************************************************************
 * 🛠️ UTILITIES
 */
const cardLog = (level, message, data = {}) => {
    if (!CARD_CONFIG.isDevelopment && level === 'info') return;
    if (typeof console !== 'undefined') {
        const logFn = console[level] || console.log;
        logFn(`🃏 [CARD] ${message}`, data);
    }
};

/*********************************************************************************************
 * 📦 PRODUCT DATA MANAGEMENT
 */
const ProductCardData = {
    /**
     * Get product data from a specific card element
     * @param {HTMLElement} card - The card element containing the product data
     */
    getFromCard(card) {
        if (!card) {
            cardLog('error', 'No card element provided');
            return null;
        }

        const scriptTag = card.querySelector('script[data-product-id]');
        if (!scriptTag) {
            cardLog('error', 'No product data script found in card');
            return null;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            if (!data || typeof data.id === 'undefined' || !Array.isArray(data.variants)) {
                cardLog('error', 'Invalid product data structure', { data });
                return null;
            }

            cardLog('info', 'Product data loaded', {
                productId: data.id,
                variantsCount: data.variants.length
            });
            return data;
        } catch (e) {
            cardLog('error', 'Failed to parse product data', { error: e.message });
            return null;
        }
    }
};

/*********************************************************************************************
 * 🪟 QUICK ADD PANEL
 */
const QuickAddPanel = {
    _trapId: null,
    _trigger: null,
    _hydrated: new Set(),

    /**
     * Fetch the panel markup of a lazy template (data-quick-add-src) once, cached by card.
     * Called on hover/focus to prefetch, then awaited on click.
     */
    _requests: new Map(),

    load(template) {
        const cardId = template.dataset.cardId;
        if (!this._requests.has(cardId)) {
            const request = fetch(template.dataset.quickAddSrc)
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.text();
                })
                .then(html => {
                    const parsed = document.createRange().createContextualFragment(html.replaceAll('__MM_QA_ID__', cardId));
                    const fetchedTemplate = parsed.querySelector(CARD_CONFIG.selectors.quickAddTemplate);
                    if (!fetchedTemplate) throw new Error('No quick-add template in response');
                    return fetchedTemplate.content;
                })
                .catch(error => {
                    this._requests.delete(cardId);
                    throw error;
                });
            this._requests.set(cardId, request);
        }
        return this._requests.get(cardId);
    },

    prefetch(card) {
        const template = card.querySelector(`${CARD_CONFIG.selectors.quickAddTemplate}[data-quick-add-src]`);
        if (template && !this._hydrated.has(card.dataset.cardId)) this.load(template).catch(() => {});
    },

    /**
     * Insert the panel for this card into document.body on first open.
     * Inline <template> (MM-Resultats, bloc CTA) is cloned; lazy one (carte produit) is fetched.
     * Scripts are re-created through a contextual fragment so the Skio picker initialises.
     * On subsequent opens, the existing hydrated panel is reused.
     */
    async hydrate(card) {
        const cardId = card.dataset.cardId;
        if (this._hydrated.has(cardId)) return true;

        const template = card.querySelector(`${CARD_CONFIG.selectors.quickAddTemplate}[data-card-id="${cardId}"]`);
        if (!template) {
            cardLog('error', 'Quick-add template not found', { cardId });
            return false;
        }

        let content = template.content;
        if (template.dataset.quickAddSrc) {
            try {
                content = await this.load(template);
            } catch (error) {
                cardLog('error', 'Quick-add panel fetch failed', { cardId, error: error.message });
                return false;
            }
            if (this._hydrated.has(cardId)) return true;
        }

        const container = document.createElement('div');
        container.appendChild(content.cloneNode(true));
        document.body.appendChild(document.createRange().createContextualFragment(container.innerHTML));
        this._hydrated.add(cardId);

        cardLog('info', 'Quick-add panel hydrated', { cardId });
        return true;
    },

    async open(card, triggerBtn = null) {
        this.closeAll();

        const cardId = card.dataset.cardId;
        triggerBtn?.setAttribute('aria-busy', 'true');
        const hydrated = await this.hydrate(card);
        triggerBtn?.removeAttribute('aria-busy');
        if (!hydrated) {
            /* Panneau indisponible (réseau) : repli sur la fiche produit */
            const productLink = card.querySelector('a[href*="/products/"]');
            if (productLink) window.location.href = productLink.href;
            return;
        }

        const panel = document.querySelector(`${CARD_CONFIG.selectors.quickAddContainer}[data-card-id="${cardId}"]`);
        if (!panel) {
            cardLog('error', 'Quick-add panel not found after hydration', { cardId });
            return;
        }

        /* rAF to allow CSS transition to play on first paint */
        requestAnimationFrame(() => {
            panel.classList.add('active');
            panel.removeAttribute('inert');
            if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
        });
        document.body.style.overflow = 'hidden';
        /* Bottom-sheet on tablet/mobile: hide Gorgias bubble so it doesn't cover the panel */
        if (window.isTablet()) document.documentElement.classList.add('mm-gorgias-hidden');

        this._trigger = triggerBtn || document.activeElement;
        if (window.FocusTrapManager) {
            this._trapId = window.FocusTrapManager.activate(panel, {
                trigger: this._trigger,
                onEscape: () => this.closeAll()
            });
        }

        cardLog('info', 'Quick-add panel opened', { cardId });
    },

    closeAll() {
        let wasOpen = false;
        document.querySelectorAll(CARD_CONFIG.selectors.quickAddContainer).forEach(panel => {
            if (panel.classList.contains('active')) wasOpen = true;
            panel.classList.remove('active');
            panel.setAttribute('inert', '');
        });
        document.body.style.overflow = '';
        /* Only restore Gorgias if a panel was open — closeAll also runs on cart:opened, where the cart keeps it hidden */
        if (wasOpen) document.documentElement.classList.remove('mm-gorgias-hidden');

        if (this._trapId && window.FocusTrapManager) {
            window.FocusTrapManager.deactivate(this._trapId);
            this._trapId = null;
            this._trigger = null;
        }
    }
};

/*********************************************************************************************
 * 🎬 EVENT DELEGATION & INITIALIZATION
 */
const ProductCardManager = {
    initialized: false,

    init() {
        if (!this.initialized) {
            this.setupEventListeners();
            this.initialized = true;
        }

        cardLog('info', 'Product card manager initialized');
    },

    setupEventListeners() {
        document.addEventListener('click', (e) => {
            const quickAddBtn = e.target.closest(CARD_CONFIG.selectors.quickAddOpen);
            if (quickAddBtn) {
                e.preventDefault();
                e.stopPropagation();
                const card = quickAddBtn.closest(CARD_CONFIG.selectors.productCard);
                if (card) QuickAddPanel.open(card, quickAddBtn);
                return;
            }

            const quickAddCloseBtn = e.target.closest(CARD_CONFIG.selectors.quickAddClose);
            if (quickAddCloseBtn) {
                e.stopPropagation();
                QuickAddPanel.closeAll();
                return;
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') QuickAddPanel.closeAll();
        });

        /* Prefetch du panneau à l'intention (survol, focus clavier, toucher) */
        const prefetch = (e) => {
            const quickAddBtn = e.target.closest?.(CARD_CONFIG.selectors.quickAddOpen);
            const card = quickAddBtn?.closest(CARD_CONFIG.selectors.productCard);
            if (card) QuickAddPanel.prefetch(card);
        };
        document.addEventListener('pointerover', prefetch, { passive: true });
        document.addEventListener('focusin', prefetch);
        document.addEventListener('touchstart', prefetch, { passive: true });
    }
};

/*********************************************************************************************
 * 🛒 CART INTEGRATION
 */
document.addEventListener('cart:add-to-cart-success', () => {
	QuickAddPanel.closeAll();
	cardLog('info', 'Quick-add panel closed after successful add to cart');
});

document.addEventListener('cart:opened', () => {
	QuickAddPanel.closeAll();
	cardLog('info', 'Quick-add panel closed when cart opened');
});

/*********************************************************************************************
 * 🧩 SKIO BUNDLE PICKER — quick-add context
 */
document.addEventListener('change', (e) => {
    const radio = e.target.closest('[data-bundle-pick]');
    if (!radio) return;
    if (!radio.closest('.mm-card-product-quick-add-container')) return;

    const form = radio.closest('form');
    if (!form) return;

    // Update variant ID in form inputs
    const newVariantId = radio.value;
    const variantInput = form.querySelector('[data-selected-variant-id]');
    if (variantInput) variantInput.value = newVariantId;
    const shopifyVariantInput = form.querySelector('[name="id"]');
    if (shopifyVariantInput) shopifyVariantInput.value = newVariantId;

    // Update dose/weight mirrors in Skio Shadow DOM
    const scopedPicker = radio.closest('skio-plan-picker');
    const sourceWeight = scopedPicker?.querySelector('[data-skio-source="weight"]');
    const sourceDose = scopedPicker?.querySelector('[data-skio-source="dose"]');
    if (sourceWeight) sourceWeight.textContent = radio.dataset.weight || '';
    if (sourceDose) sourceDose.textContent = radio.dataset.recommendedDose || '';

    // Toggle Skio subscription visibility
    const isBundle = radio.id.includes('-x2') || radio.id.includes('-x3');
    const skioPicker = form.querySelector('skio-plan-picker') || scopedPicker;
    if (skioPicker) {
        skioPicker.updateComplete?.then(() => {
            const shadow = skioPicker.shadowRoot;
            if (!shadow) return;

            let bundleSheet = shadow._mmBundleSheet;
            if (!bundleSheet) {
                bundleSheet = new CSSStyleSheet();
                shadow._mmBundleSheet = bundleSheet;
            }

            if (isBundle) {
                skioPicker.selectSellingPlanGroup(null);
                bundleSheet.replaceSync('.group-container:not(:has(input[id^="one-time-"])) { display: none !important; }');
                if (!shadow.adoptedStyleSheets.includes(bundleSheet)) {
                    shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, bundleSheet];
                }
            } else {
                bundleSheet.replaceSync('');
                const fullVariant = skioPicker.product?.variants?.find(v => v.id == newVariantId);
                if (fullVariant) {
                    const wasOnetime = !skioPicker.selectedSellingPlan;
                    skioPicker.selectedVariant = fullVariant;
                    if (wasOnetime) {
                        skioPicker.updateComplete?.then(() => skioPicker.selectSellingPlanGroup(null));
                    }
                }
            }
        });
    }
});

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ProductCardManager.init());
} else {
    ProductCardManager.init();
}

document.addEventListener('shopify:section:load', () => ProductCardManager.init());
document.addEventListener('shopify:block:select', () => ProductCardManager.init());

window.addEventListener('global:load-more-loaded', () => {
	ProductCardManager.init();
	if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
});

window.ProductCardManager = ProductCardManager;
window.ProductCardData = ProductCardData;
window.QuickAddPanel = QuickAddPanel;
